import type { RouteLocation } from 'vue-router'
import { useStorage } from '@vueuse/core'
import { getAuthStatus, requestLogin, requestLogout, requestRegister, requestSetNewPassword, requestStartPasswordReset, setApiRootUrl } from '../service'

const LOGIN_PATH = '/sign-in'
const DEFAULT_PATH = '/'

function getRedirectPathFromQuery(route: RouteLocation) {
  return route.query.next ? decodeURIComponent(route.query.next.toString()) : null
}

async function startPasswordReset(email: string) {
  const [success, errors] = await requestStartPasswordReset(email)

  if (success) {
    await navigateTo({ name: 'reset-password-request-success' })
    return null
  }
  else {
    return errors as Record<string, string[]>
  }
}

async function setNewPassword(uid: string, password: string, passwordRepeated: string) {
  const [success, errors] = await requestSetNewPassword(uid, password, passwordRepeated)

  if (success) {
    await navigateTo({ name: 'reset-password-reset-success' })
    return null
  }
  else {
    return errors as Record<string, string[]>
  }
}

export default defineNuxtPlugin(async (nuxtApp) => {
  // Skip plugin when rendering error page
  if (nuxtApp.payload.error)
    return {}

  setApiRootUrl('/_api')

  const isAuthenticated = useStorage('isAuthenticated', false)
  const currentRoute = useRoute()

  function setIsAuthenticated(newStatus: boolean) {
    isAuthenticated.value = newStatus
  }

  async function login(username: string, password: string) {
    const [success, error] = await requestLogin(username, password)

    if (success) {
      setIsAuthenticated(true)
      await navigateTo({ path: DEFAULT_PATH, query: currentRoute.query })
    }

    return error ? String(error) : null
  }

  async function logoutAndClearLocalStorage() {
    localStorage.removeItem('last-workspace')
    const [success, error] = await requestLogout()

    if (success)
      setIsAuthenticated(false)

    return error ? String(error) : null
  }

  async function register(organizationName: string, email: string, password: string, invitationCode?: string, betaAccessCode?: string) {
    const [success, errors] = await requestRegister(organizationName, email, password, invitationCode, betaAccessCode)

    if (success) {
      setIsAuthenticated(true)

      // Check if on skills host for onboarding wizard redirect
      const runtimeConfig = useRuntimeConfig()
      const isSkillsHost = runtimeConfig.public.skillsHost && window.location.host === runtimeConfig.public.skillsHost

      // New org creation on skills host (no invitation) → onboarding wizard
      // Otherwise → homepage
      const shouldShowOnboarding = isSkillsHost && !invitationCode
      await navigateTo(shouldShowOnboarding ? '/onboarding-wizard' : '/')
      return null
    }
    else {
      return errors as Record<string, string[]>
    }
  }

  const [currentAuthStatus] = await getAuthStatus()
  setIsAuthenticated(currentAuthStatus)

  addRouteMiddleware(
    'auth',
    (to) => {
      if (to.meta.auth !== false && !isAuthenticated.value) {
        if (to.meta.anonymousAllowed)
          return

        const query = to.path === DEFAULT_PATH ? {} : { next: encodeURIComponent(to.fullPath) }
        return { path: LOGIN_PATH, query }
      }
      else if (to.path === LOGIN_PATH && isAuthenticated.value) {
        return { path: DEFAULT_PATH, query: to.query }
      }
      else if (isAuthenticated.value) {
        const redirectPath = getRedirectPathFromQuery(to)
        if (redirectPath)
          return redirectPath
      }
    },
    { global: true },
  )

  watch(isAuthenticated, async (newStatus) => {
    if (newStatus === false) {
      const cachePlugin = useGqlCache()
      cachePlugin.clearCache()

      if (currentRoute.meta.auth !== false) {
        const query = { next: encodeURIComponent(currentRoute.fullPath) }
        await navigateTo({ path: LOGIN_PATH, query })
      }
    }
    else {
      const redirectPath = getRedirectPathFromQuery(currentRoute)
      if (currentRoute.path === LOGIN_PATH && redirectPath)
        await navigateTo(redirectPath)
    }
  })

  return {
    provide: {
      auth: { login, logoutAndClearLocalStorage, register, startPasswordReset, setNewPassword, isAuthenticated },
    },
  }
})
